import { useInfiniteQuery } from '@tanstack/react-query'
import { getContentByEmbedId } from '../services/contentService'
import type { ContentFilters } from '../services/contentService'

// Claves para React Query
export const infiniteContentKeys = {
  all: ['infiniteContent'] as const,
  byEmbedId: (embedId: string, encriptedString?: string, baseFilters?: Omit<ContentFilters, 'page' | 'pageSize'>) => {
    return [
      ...infiniteContentKeys.all, 
      'embedId', 
      embedId, 
      encriptedString,
      baseFilters // Filtros base sin page/pageSize
    ].filter(Boolean)
  },
}

// Hook para obtener contenido con scroll infinito usando useInfiniteQuery
export const useInfiniteContentByEmbed = (
  embedId: string, 
  encriptedString?: string, 
  baseFilters?: Omit<ContentFilters, 'page' | 'pageSize'>,
  pageSize: number = 12
) => {
  const enabled = !!embedId

  return useInfiniteQuery({
    queryKey: infiniteContentKeys.byEmbedId(embedId, encriptedString, baseFilters),
    queryFn: ({ pageParam = 1 }) => {
      // Construir filtros con la página actual
      const filters: ContentFilters = {
        ...baseFilters,
        page: pageParam,
        pageSize: pageSize
      }
      
      console.log('useInfiniteQuery fetching page:', pageParam, 'with filters:', filters)
      return getContentByEmbedId(embedId, encriptedString, filters)
    },
    getNextPageParam: (lastPage, allPages) => {
      console.log('=== getNextPageParam called ===')
      console.log('allPages.length:', allPages.length)
      console.log('lastPage:', lastPage)
      
      // Obtener los datos de la última página
      // La estructura es: lastPage.data.data donde data es el response.data de la API
      const lastPageData = lastPage?.data?.data || []
      const lastPageItemsCount = Array.isArray(lastPageData) ? lastPageData.length : 0
      
      console.log('lastPageData:', lastPageData)
      console.log('lastPageItemsCount:', lastPageItemsCount)
      console.log('pageSize:', pageSize)
      
      // Si la última página está vacía, no hay más páginas
      if (lastPageItemsCount === 0) {
        console.log('❌ No more pages: last page is empty')
        return undefined
      }
      
      // Si la última página tiene menos items que pageSize, llegamos al final
      if (lastPageItemsCount < pageSize) {
        console.log(`❌ No more pages: last page has ${lastPageItemsCount} items, less than pageSize ${pageSize}`)
        return undefined
      }
      
      // Si la última página tiene exactamente pageSize items, puede haber más
      // Continuar intentando cargar la siguiente página
      const nextPage = allPages.length + 1
      console.log(`✅ More pages available: last page has ${lastPageItemsCount} items (pageSize: ${pageSize}), will load page ${nextPage}`)
      
      return nextPage
    },
    initialPageParam: 1,
    enabled,
    staleTime: 30 * 1000, // 30 segundos de cache
    gcTime: 5 * 60 * 1000, // 5 minutos en memoria
    retry: (failureCount, error: any) => {
      // No reintentar si es error de autenticación
      if (error?.response?.status === 401) {
        return false
      }
      return failureCount < 3
    },
    refetchOnWindowFocus: false,
  })
}

export default useInfiniteContentByEmbed
